// Env-var reads must use string-literal keys so react-native-dotenv can inline
// them at babel-compile time on mobile. Dynamic keys (e.g. process.env[`X_${k}`])
// are NOT inlined and resolve to undefined at runtime on RN/Hermes.
const readImageBase = (): string | undefined =>
  process.env.REACT_APP_IMAGE_BASE || process.env.IMAGE_BASE

const readVideoBase = (): string | undefined =>
  process.env.REACT_APP_VIDEO_BASE || process.env.VIDEO_BASE

const requireEnv = (name: string, value: string | undefined): string => {
  if (!value) {
    throw new Error(
      `Missing env var: ${name} (or REACT_APP_${name}). ` +
        'Set it in the active .env file.'
    )
  }
  return value
}

export const getImageBaseUrl = (): string =>
  requireEnv('IMAGE_BASE', readImageBase())

export const getVideoBaseUrl = (): string =>
  requireEnv('VIDEO_BASE', readVideoBase())

const joinPath = (base: string, ...parts: string[]): string => {
  const trimmedBase = base.replace(/\/+$/, '')
  const cleanParts = parts.map((p) => p.replace(/^\/+/, '').replace(/\/+$/, ''))
  return [trimmedBase, ...cleanParts].join('/')
}

/**
 * Bridge layer: rewrites known S3 asset hosts in absolute URLs to the active
 * image base from env. Keeps the rest of the URL (path + query) intact.
 *
 * Remove this once BE returns full_path values pointing at the configured CDN.
 */
const KNOWN_S3_HOSTS = [
  'sportninja-images-production.s3.amazonaws.com',
  'canlan-images-production.s3.amazonaws.com',
]

export const rewriteAssetHost = (
  url: string,
  baseUrl: string = getImageBaseUrl()
): string => {
  if (!url || typeof url !== 'string') {
    return url
  }
  for (const host of KNOWN_S3_HOSTS) {
    const idx = url.indexOf(host)
    if (idx === -1) {
      continue
    }
    const trimmedBase = baseUrl.replace(/\/+$/, '')
    const rest = url.slice(idx + host.length)
    return `${trimmedBase}${rest}`
  }
  return url
}

export const buildImageUrl = (
  folder: 'original' | 'resized',
  filename: string
): string => joinPath(getImageBaseUrl(), folder, filename)

export const buildVideoUrl = (filename: string): string =>
  joinPath(getVideoBaseUrl(), 'videos', filename)

/**
 * Any image-like value the API may return:
 * - flat URL string (e.g. `team.image_url`, `highlight.thumbnail_url`)
 * - image object with `full_path` (preferred) or `url`
 */
export type ImageLike =
  | string
  | {
      full_path?: string | null
      url?: string | null
    }
  | null
  | undefined

/**
 * Resolve any image-like value to a final CDN URL.
 *
 * - String input → rewrites known S3 host to active image base.
 * - Object input → reads `full_path` (preferred) or `url`, then rewrites host.
 * - Falsy / unrecognized shape → returns `undefined` (JSX `src=` safe).
 */
export const resolveAssetUrl = (image: ImageLike): string | undefined => {
  if (!image) {
    return undefined
  }
  if (typeof image === 'string') {
    return rewriteAssetHost(image)
  }
  const path = image.full_path || image.url
  if (!path) {
    return undefined
  }
  return rewriteAssetHost(path)
}

/**
 * Resolve a video URL with care for livestream playback.
 *
 * - Falsy input → returned unchanged.
 * - Bare filename (no scheme) → built via `buildVideoUrl` against the video
 *   asset base (e.g. recorded highlight filenames).
 * - Known S3 asset host → rewritten to the active video base (recorded
 *   broadcasts, uploaded highlights).
 * - Any other absolute URL (HLS .m3u8, mux.com, YouTube, etc.) → returned
 *   unchanged so livestream playback URLs are never touched.
 */
const matchesKnownAssetHost = (url: string): boolean =>
  KNOWN_S3_HOSTS.some((host) => url.includes(host))

export const resolveVideoUrl = (
  url: string | null | undefined
): string | undefined => {
  if (!url || typeof url !== 'string') {
    return undefined
  }
  if (/^https?:\/\//i.test(url)) {
    if (matchesKnownAssetHost(url)) {
      return rewriteAssetHost(url, getVideoBaseUrl())
    }
    return url
  }
  return buildVideoUrl(url)
}
